import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TransactionList = ({ transactions, onCategorize, onCreateCategory, loading = false }) => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const categoryOptions = [
    { value: 'groceries', label: 'Groceries', description: 'Food and household items' },
    { value: 'dining', label: 'Dining & Restaurants', description: 'Eating out and takeout' },
    { value: 'transportation', label: 'Transportation', description: 'Gas, parking, public transit' },
    { value: 'utilities', label: 'Utilities', description: 'Electric, water, internet' },
    { value: 'entertainment', label: 'Entertainment', description: 'Movies, games, hobbies' },
    { value: 'healthcare', label: 'Healthcare', description: 'Medical expenses and insurance' },
    { value: 'shopping', label: 'Shopping', description: 'Clothing, electronics, general retail' },
    { value: 'income', label: 'Income', description: 'Salary, freelance, other income' },
    { value: 'transfer', label: 'Transfer', description: 'Account transfers' },
    { value: 'uncategorized', label: 'Uncategorized', description: 'Not yet categorized' }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(Math.abs(amount));
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getCategoryIcon = (category) => {
    const icons = {
      groceries: 'ShoppingCart',
      dining: 'Utensils',
      transportation: 'Car',
      utilities: 'Zap',
      entertainment: 'Film',
      healthcare: 'Heart',
      shopping: 'ShoppingBag',
      income: 'TrendingUp',
      transfer: 'ArrowLeftRight',
      uncategorized: 'HelpCircle'
    };
    return icons?.[category] || 'Circle';
  };

  const getCategoryColor = (category) => {
    const colors = {
      groceries: 'text-green-600',
      dining: 'text-orange-600',
      transportation: 'text-blue-600',
      utilities: 'text-yellow-600',
      entertainment: 'text-purple-600',
      healthcare: 'text-red-600',
      shopping: 'text-pink-600',
      income: 'text-emerald-600',
      transfer: 'text-gray-600',
      uncategorized: 'text-muted-foreground'
    };
    return colors?.[category] || 'text-muted-foreground';
  };

  const handleCategoryChange = (transactionId, newCategory) => {
    onCategorize(transactionId, newCategory);
    setSelectedTransaction(null);
  };

  if (loading) {
    return (
      <div className="space-y-3 md:space-y-4">
        {[...Array(5)]?.map((_, i) => (
          <div key={i} className="bg-card rounded-xl p-3 md:p-4 border border-border animate-pulse">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-gradient-to-br from-gray-200 to-gray-300 flex-shrink-0" />
              <div className="flex-1 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-3/4 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                    <div className="h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded w-1/2 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                  </div>
                  <div className="w-20 h-6 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                  <div className="w-20 h-8 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      {transactions?.map((transaction, index) => (
        <div
          key={transaction?.id}
          className="bg-card rounded-xl p-3 md:p-4 border border-border hover:shadow-elevation-3 transition-all duration-300 hover:scale-[1.01] hover:-translate-y-0.5 cursor-pointer group animate-fade-in"
          style={{ animationDelay: `${index * 0.05}s` }}
        >
          <div className="flex items-start gap-3 md:gap-4">
            <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl bg-muted flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 ${getCategoryColor(transaction?.category)}`}>
              <Icon name={getCategoryIcon(transaction?.category)} size={20} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm md:text-base font-semibold text-foreground truncate transition-colors duration-200 group-hover:text-primary">
                    {transaction?.merchant}
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground">
                    {formatDate(transaction?.date)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className={`text-base md:text-lg font-bold transition-all duration-200 group-hover:scale-105 ${
                    transaction?.amount < 0 ? 'text-error' : 'text-success'
                  }`}>
                    {transaction?.amount < 0 ? '-' : '+'}{formatCurrency(transaction?.amount)}
                  </p>
                </div>
              </div>

              {transaction?.description && (
                <p className="text-xs md:text-sm text-muted-foreground mb-3 line-clamp-2">
                  {transaction?.description}
                </p>
              )}

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 md:gap-3">
                {selectedTransaction === transaction?.id ? (
                  <div className="flex items-center gap-2 flex-1 w-full sm:w-auto animate-slide-up">
                    <Select
                      options={categoryOptions}
                      value={selectedCategory}
                      onChange={setSelectedCategory}
                      placeholder="Select category..."
                      searchable
                      className="flex-1"
                    />
                    <Button
                      variant="default"
                      size="sm"
                      iconName="Check"
                      onClick={() => handleCategoryChange(transaction?.id, selectedCategory)}
                      disabled={!selectedCategory}
                    >
                      Save
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="X"
                      onClick={() => {
                        setSelectedTransaction(null);
                        setSelectedCategory('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg transition-all duration-200 hover:bg-muted/80 hover:scale-105">
                      <Icon name={getCategoryIcon(transaction?.category)} size={14} className={getCategoryColor(transaction?.category)} />
                      <span className="text-xs md:text-sm font-medium text-foreground capitalize">
                        {transaction?.category?.replace('_', ' ')}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit2"
                      iconPosition="left"
                      onClick={() => {
                        setSelectedTransaction(transaction?.id);
                        setSelectedCategory(transaction?.category);
                      }}
                      className="text-xs"
                    >
                      Recategorize
                    </Button>
                    {transaction?.isRecurring && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded text-xs font-medium transition-all duration-200 hover:scale-110">
                        <Icon name="Repeat" size={12} />
                        <span>Recurring</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {transaction?.mlSuggestion && transaction?.category === 'uncategorized' && (
                <div className="mt-3 p-2 md:p-3 bg-primary/10 border border-primary/20 rounded-lg transition-all duration-300 hover:bg-primary/15 hover:border-primary/30 animate-slide-up">
                  <div className="flex items-start gap-2">
                    <Icon name="Sparkles" size={16} className="text-primary flex-shrink-0 mt-0.5 animate-pulse-slow" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs md:text-sm text-foreground">
                        <span className="font-semibold">AI Suggestion:</span> {transaction?.mlSuggestion?.category}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {transaction?.mlSuggestion?.confidence}% confidence
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCategoryChange(transaction?.id, transaction?.mlSuggestion?.category?.toLowerCase())}
                      className="text-xs flex-shrink-0"
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default TransactionList;